/*
 * Bundelkiezer voor de 60 ml flacons
 * -----------------------------------------------------------------------------
 * Vervangt de app Fly Bundles & Quantity Breaks. De klant kiest een trede (1, 2
 * of 3 flacons) en per flacon een geur; bij drie flacons komt daar een gratis
 * reisformaat bij.
 *
 * De korting zelf zit hier niet in. Die komt uit de discount function, die telt
 * op aantal in het mandje. Dit bestand voegt alleen de juiste regels toe, en
 * hangt aan de cadeauregel het kenmerk _bundle_gift zodat de function hem kan
 * herkennen.
 */

(function () {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /*
   * De travel size-collectie levert twee dingen tegelijk: de miniaturen voor de
   * geurlijst en de keuzes voor het cadeau. Eén keer ophalen dus, en het
   * verzoek zelf onthouden zodat twee slots die tegelijk openen niet allebei
   * gaan halen.
   */
  const collectionRequests = new Map();

  function fetchCollection(handle) {
    if (!handle) return Promise.resolve([]);
    if (collectionRequests.has(handle)) return collectionRequests.get(handle);

    const request = fetch(ROOT + 'collections/' + handle + '/products.json?limit=250')
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .then((data) => data.products || [])
      .catch(() => []);

    collectionRequests.set(handle, request);
    return request;
  }

  function normalise(value) {
    return String(value || '').trim().toLowerCase();
  }

  /* Vraagt de CDN om een kleiner formaat; scheelt bij 115 rijen aardig wat. */
  function sized(url) {
    if (!url) return '';
    if (url.indexOf('cdn.shopify.com') === -1) return url;
    return url.replace(/(\.[a-z]+)(\?|$)/i, '_112x$1$2');
  }

  class BundlePicker extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-fragrance-dialog]');
      this.list = this.querySelector('[data-fragrance-list]');
      this.rowTemplate = this.querySelector('[data-fragrance-row]');
      this.searchInput = this.querySelector('[data-fragrance-search]');
      this.tabs = this.querySelector('[data-fragrance-tabs]');
      this.emptyNote = this.querySelector('[data-fragrance-empty]');
      this.errorNote = this.querySelector('[data-bundle-error]');

      this.giftHandle = this.dataset.giftCollection || '';
      this.bottleImage = this.dataset.bottleImage || '';
      this.giftLoaded = false;
      this.genderLabels = {
        Women: this.dataset.labelWomen || 'Women',
        Men: this.dataset.labelMen || 'Men',
        Unisex: this.dataset.labelUnisex || 'Unisex',
      };

      this.fragrances = this.readFragrances();
      this.giftProducts = [];
      this.filter = '';
      this.query = '';
      this.activeSlot = null;
      this.giftMode = false;

      this.paintCounts();
      this.bindEvents();
      this.watchStickyBar();

      // Staat trede 3 al voorgeselecteerd, dan de cadeaukeuzes vast ophalen.
      const checked = this.querySelector('.bundle-tier__radio:checked');
      if (checked && checked.value === '3') this.loadGiftProducts();
    }

    /*
     * De vaste balk op mobiel verschijnt zodra de echte knop uit beeld is. Met
     * een observer en niet op scrollpositie: dat blijft kloppen als de kiezer
     * van hoogte verandert doordat iemand een andere trede kiest.
     */
    watchStickyBar() {
      const bar = document.querySelector('[data-sticky-bar]');
      const button = this.querySelector('[data-bundle-add]');
      if (!bar || !button || typeof IntersectionObserver !== 'function') return;

      bar.hidden = false;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            bar.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { rootMargin: '0px 0px -40px 0px' }
      );

      observer.observe(button);

      bar.addEventListener('click', (event) => {
        if (!event.target.closest('[data-sticky-add]')) return;
        event.preventDefault();
        // addToCart brengt de klant zelf naar het eerste lege slot.
        this.addToCart();
      });
    }

    readFragrances() {
      const holder = this.querySelector('[data-fragrances]');
      if (!holder) return [];
      try {
        return JSON.parse(holder.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    /*
     * De cadeaukeuzes komen uit de travel size-collectie. Pas ophalen als de
     * klant die kant op gaat — bij trede 3 of bij het openen van het
     * cadeauslot — zodat wie gewoon één flacon koopt er geen verzoek voor over
     * heeft.
     */
    loadGiftProducts() {
      if (!this.giftHandle || this.giftLoaded) return;
      this.giftLoaded = true;

      fetchCollection(this.giftHandle).then((products) => {
        this.giftProducts = products;
        // Staat het cadeauslot al open, dan alsnog opnieuw tekenen.
        if (this.giftMode && this.dialog && this.dialog.open) this.renderList();
      });
    }

    bindEvents() {
      this.addEventListener('change', (event) => {
        const radio = event.target.closest('.bundle-tier__radio');
        if (radio) this.selectTier(radio);
      });

      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-open-picker]');
        if (opener) {
          event.preventDefault();
          this.openPicker(opener);
          return;
        }

        if (event.target.closest('[data-close-picker]')) {
          event.preventDefault();
          this.closePicker();
          return;
        }

        const tab = event.target.closest('[data-filter]');
        if (tab) {
          event.preventDefault();
          this.setFilter(tab);
          return;
        }

        const choose = event.target.closest('.fragrance-row__choose');
        if (choose) {
          event.preventDefault();
          this.choose(choose.closest('.fragrance-row'));
          return;
        }

        if (event.target.closest('[data-bundle-add]')) {
          event.preventDefault();
          this.addToCart();
        }
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => {
          this.query = normalise(this.searchInput.value);
          this.renderList();
        });
      }

      // Klikken op de achtergrond van de dialog sluit hem, klikken erin niet.
      if (this.dialog) {
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.closePicker();
        });
        this.dialog.addEventListener('close', () => this.restoreFocus());
      }
    }

    selectTier(radio) {
      if (!radio) return;
      this.querySelectorAll('.bundle-tier').forEach((tier) => {
        tier.classList.toggle('is-selected', tier.dataset.tier === radio.value);
      });
      this.hideError();

      // Trede 3 heeft het cadeauslot; die keuzes vast klaarzetten.
      if (radio.value === '3') this.loadGiftProducts();
    }

    get selectedTier() {
      return this.querySelector('.bundle-tier.is-selected');
    }

    openPicker(button) {
      this.activeSlot = button.closest('[data-slot]');
      this.giftMode = button.hasAttribute('data-gift-picker');
      this.query = '';
      this.filter = '';

      if (this.giftMode) this.loadGiftProducts();

      if (this.searchInput) this.searchInput.value = '';
      if (this.tabs) this.tabs.hidden = this.giftMode;
      this.syncTabs();
      this.renderList();

      if (!this.dialog) return;
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      // Op mobiel opent anders meteen het toetsenbord over de lijst heen.
      if (this.searchInput && window.matchMedia('(min-width: 750px)').matches) {
        this.searchInput.focus();
      }
    }

    closePicker() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.restoreFocus();
      }
    }

    restoreFocus() {
      const button = this.activeSlot && this.activeSlot.querySelector('[data-open-picker]');
      if (button) button.focus();
    }

    setFilter(tab) {
      this.filter = tab.dataset.filter || '';
      this.syncTabs();
      this.renderList();
    }

    syncTabs() {
      if (!this.tabs) return;
      this.tabs.querySelectorAll('[data-filter]').forEach((tab) => {
        const active = (tab.dataset.filter || '') === this.filter;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    paintCounts() {
      const totals = { '': this.fragrances.length, Women: 0, Men: 0, Unisex: 0 };
      this.fragrances.forEach((fragrance) => {
        if (totals[fragrance.gender] !== undefined) totals[fragrance.gender] += 1;
      });

      this.querySelectorAll('[data-count-for]').forEach((node) => {
        const value = totals[node.dataset.countFor];
        node.textContent = value ? ' ' + value : '';
      });
    }

    /* De keuzes van dit moment: geuren, of de cadeauproducten. */
    get options() {
      if (!this.giftMode) return this.fragrances;

      // De collectie komt in eigen volgorde binnen; op naam, net als de geuren.
      return this.giftProducts
        .map((product) => {
          const variant = (product.variants || []).find((entry) => entry.available);
          if (!variant) return null;
          const image = product.images && product.images[0];
          return {
            id: variant.id,
            name: product.title,
            gender: '',
            image: image ? image.src : null,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    renderList() {
      if (!this.list || !this.rowTemplate) return;

      const matches = this.options.filter((option) => {
        if (this.filter && option.gender !== this.filter) return false;
        if (this.query && normalise(option.name).indexOf(this.query) === -1) return false;
        return true;
      });

      const fragment = document.createDocumentFragment();

      matches.forEach((option) => {
        const row = this.rowTemplate.content.firstElementChild.cloneNode(true);
        row.dataset.variantId = option.id;
        row.dataset.name = option.name;

        /*
         * Bij de geuren staat overal dezelfde flacon van 60 ml. Dat is precies
         * wat de klant koopt; de travel size-foto's tonen een ander formaat en
         * lezen daardoor als het verkeerde product. Alleen bij het cadeau klopt
         * de eigen productfoto wél, want dat ís een reisformaat.
         */
        const source = this.giftMode ? sized(option.image) : this.bottleImage;
        const image = row.querySelector('img');

        if (source) {
          image.src = source;
          row.dataset.image = source;
        } else {
          image.remove();
        }

        row.querySelector('.fragrance-row__name').textContent = option.name;

        const pill = row.querySelector('.fragrance-row__pill');
        if (option.gender) {
          pill.textContent = this.genderLabels[option.gender] || option.gender;
        } else {
          pill.remove();
        }

        fragment.appendChild(row);
      });

      this.list.replaceChildren(fragment);
      if (this.emptyNote) this.emptyNote.hidden = matches.length > 0;
    }

    choose(row) {
      if (!row || !this.activeSlot) return;

      const label = this.activeSlot.querySelector('[data-slot-label]');
      const input = this.activeSlot.querySelector('[data-slot-variant]');
      const thumb = this.activeSlot.querySelector('[data-slot-thumb]');

      if (label) label.textContent = row.dataset.name;
      if (input) input.value = row.dataset.variantId;
      /*
       * Alleen de foto vervangen, niet de hele miniatuur leegmaken: daarin zit
       * ook het label (#1, #2 of FREE) dat aangeeft om welke fles het gaat.
       */
      if (thumb) {
        const existing = thumb.querySelector('img');

        if (row.dataset.image) {
          if (existing) {
            existing.src = row.dataset.image;
          } else {
            const image = document.createElement('img');
            image.src = row.dataset.image;
            image.alt = '';
            image.width = 34;
            image.height = 44;
            thumb.insertBefore(image, thumb.firstChild);
          }
        } else if (existing) {
          existing.remove();
        }
      }

      this.activeSlot.classList.add('is-filled');
      this.hideError();
      this.closePicker();
    }

    showError(message) {
      if (!this.errorNote) return;
      this.errorNote.textContent = message;
      this.errorNote.hidden = false;
    }

    hideError() {
      if (this.errorNote) this.errorNote.hidden = true;
    }

    addToCart() {
      const tier = this.selectedTier;
      if (!tier) return;

      const slots = Array.from(tier.querySelectorAll('[data-slot]'));
      const items = [];
      let incomplete = false;

      slots.forEach((slot) => {
        const input = slot.querySelector('[data-slot-variant]');
        const value = input && input.value;

        if (!value) {
          // Het cadeauslot mag leeg blijven: dan hoeft de klant het niet.
          if (!slot.hasAttribute('data-gift-slot')) incomplete = true;
          slot.classList.toggle('has-error', !slot.hasAttribute('data-gift-slot'));
          return;
        }

        slot.classList.remove('has-error');

        const item = { id: Number(value), quantity: 1 };
        if (slot.hasAttribute('data-gift-slot')) {
          /*
           * Merkt de cadeauregel voor de discount function. De underscore houdt
           * hem uit het zicht in mandje en checkout. De waarde zegt niets: of
           * het cadeau echt gratis is beslist de function, aan de hand van het
           * aantal flacons.
           */
          item.properties = { _bundle_gift: '1' };
        }
        items.push(item);
      });

      if (incomplete) {
        this.showError(this.dataset.errorIncomplete || 'Please choose a fragrance for every bottle');

        /*
         * Naar het eerste lege slot brengen. Vanaf de vaste balk onderaan staat
         * de kiezer meestal buiten beeld; alleen een melding tonen ziet er dan
         * uit alsof de knop niets doet.
         *
         * Focus pas na het scrollen: focus() springt anders meteen, en dan zie
         * je de beweging niet.
         */
        const firstEmpty = slots.find((slot) => {
          const input = slot.querySelector('[data-slot-variant]');
          return !slot.hasAttribute('data-gift-slot') && !(input && input.value);
        });

        if (firstEmpty) {
          firstEmpty.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const opener = firstEmpty.querySelector('[data-open-picker]');
          if (opener) window.setTimeout(() => opener.focus({ preventScroll: true }), 400);
        }
        return;
      }
      if (!items.length) return;

      const button = this.querySelector('[data-bundle-add]');
      if (button) button.disabled = true;

      const done = () => {
        if (button) button.disabled = false;
      };

      if (window.SentirCart && typeof window.SentirCart.add === 'function') {
        window.SentirCart.add({ items: items }).then(done, done);
        return;
      }

      // Zonder de drawer alsnog toevoegen en doorsturen naar het mandje.
      fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(() => {
          window.location.href = ROOT + 'cart';
        })
        .catch(done);
    }
  }

  if (!customElements.get('bundle-picker')) {
    customElements.define('bundle-picker', BundlePicker);
  }

  /*
   * Trustpilot in een dichtgeklapt paneel.
   *
   * De widget meet zichzelf bij het laden op; staat hij dan in een gesloten
   * <details>, dan is dat nul bij nul en blijft het vak leeg — ook nadat je
   * het paneel opent. Daarom wordt hij pas gemount bij het openklappen.
   *
   * Het toggle-event bubbelt niet, dus per paneel een eigen listener. De vlag
   * gaat er pas op als het mounten echt is gelukt: laadt het script van
   * Trustpilot nog, dan proberen we het bij de volgende keer opnieuw.
   */
  function mountDeferredTrustpilot(scope) {
    const widget = scope.querySelector('[data-tp-defer]:not([data-tp-mounted])');
    if (!widget) return;
    if (!window.Trustpilot || typeof window.Trustpilot.loadFromElement !== 'function') return;

    window.Trustpilot.loadFromElement(widget, true);
    widget.setAttribute('data-tp-mounted', '');
  }

  function watchDeferredWidgets() {
    document.querySelectorAll('details').forEach((panel) => {
      if (!panel.querySelector('[data-tp-defer]')) return;

      panel.addEventListener('toggle', () => {
        if (!panel.open) return;
        mountDeferredTrustpilot(panel);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDeferredWidgets);
  } else {
    watchDeferredWidgets();
  }
})();
